import React from "react";
import { CheckOutlined, MessageOutlined, SendOutlined } from "@ant-design/icons";
import { Button, Col, Divider, Flex, Form, Input, Row, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { makeSelectLanguage, makeSelectUserInfo } from "../../../../containers/App/selectors";
import globalSlice from "../../../../containers/App/reducer";
import { OFFICE_TYPES } from "../../../../dto/Company/CompanySettingsDto";
import DealNotificationModel, { DealNotificationUtils } from "../../../../viewModels/Notification/DealNotificationModel";
import dealNotificationModalSlice from "../../../../widgets/DealNotificationModal/reducer";
import AdminPanelDealLink from "../../../../containers/AdminPanelPages/components/AdminPanelDealLink";
import DealLink from "../../../DealLink";
import type { SendDealNotificationResponseFormInput } from "../../types";
import DealNotificationStatusTag from "../DealNotificationStatusTag";
import DealQuestionDeliveryStatus from "../DealQuestionDeliveryStatus";
import { makeSelectLoading, selectCreatedQuestionRequestId } from "../../../../widgets/DealNotificationModal/selectors";

type Props = {
  notification: DealNotificationModel;
  adminOfficeType?: OFFICE_TYPES;
};

const formattedTextStyle: React.CSSProperties = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const DealNotificationListItem = React.memo(({ notification, adminOfficeType }: Props) => {
  const { t } = useTranslation("translations");

  const dispatch = useDispatch();

  const userInfo = useSelector(makeSelectUserInfo());
  const lang = useSelector(makeSelectLanguage());
  const createdRequestId = useSelector(selectCreatedQuestionRequestId);
  const creatingQuestion = useSelector(makeSelectLoading);
  const officeType = notification.officeType || adminOfficeType;
  const useAdminLink = Boolean(adminOfficeType || (officeType && officeType !== userInfo.company.settings.officeType));
  const submission = React.useRef<{ signature: string; id: string }>();

  const [continueFormOpen, setContinueFormOpen] = React.useState(false);
  const [followUpRequest, setFollowUpRequest] = React.useState("");

  const handleFinishSendResponse = (model: SendDealNotificationResponseFormInput) => {
    dispatch(globalSlice.actions.sendDealNotificationResponse({ ...model, officeType }));
  };

  const handleCloseDealNotification = React.useCallback(() => {
    dispatch(globalSlice.actions.closeDealNotification({ id: notification.id, officeType }));
  }, [dispatch, notification.id, officeType]);

  const handleCreateFollowUp = React.useCallback(() => {
    const signature = JSON.stringify({ id: notification.id, officeType, request: followUpRequest });
    if (submission.current?.signature !== signature) submission.current = { signature, id: window.crypto.randomUUID() };
    dispatch(
      dealNotificationModalSlice.actions.createDealNotification({
        requestId: submission.current.id,
        officeType,
        dealId: notification.deal.id,
        request: followUpRequest,
        recipientIds: DealNotificationUtils.getRecipientIds(notification),
        closeNotificationId: notification.id,
      }),
    );
  }, [dispatch, followUpRequest, notification, officeType]);

  React.useEffect(() => {
    if (createdRequestId && createdRequestId === submission.current?.id) {
      setFollowUpRequest("");
      setContinueFormOpen(false);
      submission.current = undefined;
    }
  }, [createdRequestId]);

  return (
    <Row>
      <Col span={16}>
        <Typography.Title level={5}>
          {t("request")} {notification.id}, {t("deal")}{" "}
          {useAdminLink ? (
            <AdminPanelDealLink id={notification.deal.id} officeType={officeType!}>
              {notification.deal.number}
            </AdminPanelDealLink>
          ) : (
            <DealLink id={notification.deal.id} number={notification.deal.number} />
          )}{" "}
          <DealNotificationStatusTag status={notification.notificationStatus} />
        </Typography.Title>
        <Flex vertical gap={0}>
          <Space>
            <Typography.Text strong>{notification.requester.name}</Typography.Text>
            <Typography.Text type="secondary">{notification.creationDate.locale(lang).format("DD MMMM YYYY HH:mm")}</Typography.Text>
          </Space>
          <Typography.Text style={formattedTextStyle}>{notification.request}</Typography.Text>
          <DealQuestionDeliveryStatus notification={notification} />
        </Flex>

        {notification.notificationStatus === "REQUESTED" && DealNotificationUtils.isRequestRecipient(notification, userInfo.id) && (
          <>
            <Divider size="small">{t("response")}</Divider>
            <Form layout="vertical" initialValues={{ id: notification.id }} onFinish={handleFinishSendResponse}>
              <Form.Item name="id" noStyle>
                <Input hidden />
              </Form.Item>
              <Form.Item name="response" label={t("response")} rules={[{ required: true, message: t("enter_response") }]}>
                <Input.TextArea autoSize />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button htmlType="submit" type="primary" icon={<SendOutlined />}>
                  {t("send")}
                </Button>
              </Form.Item>
            </Form>
          </>
        )}
        {(notification.notificationStatus === "RESPONDED" || notification.notificationStatus === "CLOSED") && (
          <>
            <Divider size="small"></Divider>
            <Flex vertical>
              <Space>
                <Typography.Text strong>{(notification.answeredBy || notification.manager).name}</Typography.Text>
                <Typography.Text type="secondary">
                  {notification.responseDate && notification.responseDate.locale(lang).format("DD MMMM YYYY HH:mm")}
                </Typography.Text>
              </Space>
              <Typography.Text style={formattedTextStyle}>{notification.response}</Typography.Text>
            </Flex>
          </>
        )}
        {notification.notificationStatus === "RESPONDED" && notification.requester.id === userInfo.id && (
          <>
            <Divider size="small" />
            <Space orientation="vertical" style={{ width: "100%" }}>
              <Space>
                <Button type="primary" icon={<CheckOutlined />} onClick={handleCloseDealNotification}>
                  {t("close")}
                </Button>
                <Button icon={<MessageOutlined />} onClick={() => setContinueFormOpen((value) => !value)}>
                  {t("continue_conversation")}
                </Button>
              </Space>
              {continueFormOpen && (
                <Space.Compact style={{ width: "100%" }}>
                  <Input.TextArea
                    autoSize
                    placeholder={t("request")}
                    value={followUpRequest}
                    onChange={(e) => setFollowUpRequest(e.target.value)}
                  />
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={creatingQuestion}
                    disabled={!followUpRequest.trim()}
                    onClick={handleCreateFollowUp}
                  >
                    {t("send")}
                  </Button>
                </Space.Compact>
              )}
            </Space>
          </>
        )}
      </Col>
      <Col span={8}>
        <Space orientation="vertical" size={8} style={{ width: "100%" }}>
          <Flex vertical align="flex-end">
            <Typography.Text type="secondary">{t("producer")}</Typography.Text>
            <Typography.Text strong>{notification.requester.name}</Typography.Text>
          </Flex>
          <Flex vertical align="flex-end">
            <Typography.Text type="secondary">{t("manager")}</Typography.Text>
            <Typography.Text strong>{notification.manager.name}</Typography.Text>
          </Flex>
        </Space>
      </Col>
    </Row>
  );
});

export default DealNotificationListItem;
